/* eslint-disable no-magic-numbers */
import React, { ChangeEvent, FC, useMemo } from 'react';
import { useIntl } from '@futrli/localisation';

import { Option, Select } from 'carbon-react/lib/components/select';

export enum PERIODS {
  THIS_MONTH = 'this_month',
  THIS_QUARTER = 'this_quarter',
  THIS_YEAR = 'this_year',
  LAST_MONTH = 'last_month',
  LAST_QUARTER = 'last_quarter',
  LAST_YEAR = 'last_year',
  LAST_3_MONTHS = 'last_3_months',
  LAST_6_MONTHS = 'last_6_months',
  LAST_12_MONTHS = 'last_12_months',
  LAST_24_MONTHS = 'last_24_months'
}

interface Props {
  period: PERIODS;
  disabled?: boolean;
  onChange?: (period: PERIODS) => unknown;
  onlyCurrentPeriods?: boolean;
}

export const PeriodDropdown: FC<Props> = ({ period, disabled, onChange, onlyCurrentPeriods }: Props) => {
  const intl = useIntl();
  const periodOptions = useMemo(() => {
    const options = [
      {
        name: intl.formatMessage({ id: 'reporting.period.this_month', defaultMessage: 'This month' }),
        value: 'this_month'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_quarter', defaultMessage: 'This quarter' }),
        value: 'this_quarter'
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_fin_year', defaultMessage: 'This financial year' }),
        value: 'this_year'
      }
    ];
    if (!onlyCurrentPeriods) {
      options.push(
        {
          name: intl.formatMessage({ id: 'common.last_month', defaultMessage: 'Last month' }),
          value: 'last_month'
        },
        {
          name: intl.formatMessage({ id: 'reporting.period.last_quarter', defaultMessage: 'Last quarter' }),
          value: 'last_quarter'
        },
        {
          name: intl.formatMessage({ id: 'reporting.period.last_fin_year', defaultMessage: 'Last financial year' }),
          value: 'last_year'
        }
      );
    }
    return options;
  }, [onlyCurrentPeriods, intl]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value as PERIODS);
    }
  };

  return (
    <div data-testid="period-selector">
      <Select
        size="medium"
        value={period}
        autoComplete="off"
        onChange={handleOnChange}
        style={{ minWidth: 150 }}
        listMaxHeight={periodOptions.length * 32}
        disabled={disabled}
        ariaLabel={intl.formatMessage({
          id: 'predict.performance.period_selector',
          defaultMessage: 'Period selector'
        })}
      >
        {periodOptions.map(option => (
          <Option key={option.value} value={option.value} text={option.name} />
        ))}
      </Select>
    </div>
  );
};
